// src/pages/finance/payroll/PayrollSettingsSection.jsx
// Clean, open "free layout" without enclosing card/box container.

import React, { useState, useEffect } from "react";
import { getPayrollSettings, updatePayrollSettings } from "../../../services/payrollService";

export default function PayrollSettingsSection() {
  const [settings, setSettings] = useState({
    fullTimeSalary: 30000,
    fresherStipend: 8000,
    experiencedStipend: 15000,
    deferredStipend: 8000,
    pfEmployee: 12,
    pfEmployer: 12,
    overtimeRate: 100,
    bonus: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const data = await getPayrollSettings();
    if (data) setSettings(data);
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccessMsg("");
    const res = await updatePayrollSettings(settings);
    setSaving(false);
    if (res?.success || res === true) {
      const count = typeof res === "object" && res.updatedCount !== undefined ? res.updatedCount : 0;
      setSuccessMsg(
        count > 0
          ? `Payroll settings saved successfully! Recalculated and updated ${count} generated payroll record(s) for all employees.`
          : "Payroll settings saved successfully! All generated payrolls are in sync."
      );
      setTimeout(() => setSuccessMsg(""), 5000);
    } else {
      alert("Failed to save settings. Please try again.");
    }
  };

  const inputStyle = {
    width: "100%",
    background: "#FFFFFF",
    border: "1px solid #D9DEE3",
    borderRadius: "6px",
    padding: "9px 12px",
    fontSize: "0.875rem",
    color: "#1E293B",
    boxSizing: "border-box",
    outline: "none",
  };

  const labelStyle = {
    display: "block",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "#334155",
    marginBottom: "0.45rem",
  };

  const sectionTitleStyle = {
    fontSize: "1.05rem",
    fontWeight: 700,
    color: "#1E293B",
    margin: "0 0 1rem",
  };

  if (loading) {
    return <div style={{ padding: "20px 0", color: "#64748B" }}>Loading payroll settings...</div>;
  }

  return (
    <div style={{ width: "100%", maxWidth: 1200, padding: "4px 0" }}>
      {/* Header info */}
      <div style={{ marginBottom: "1.75rem" }}>
        <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#164231", margin: "0 0 0.4rem" }}>
          Payroll Settings
        </h2>
        <p style={{ margin: 0, color: "#64748B", fontSize: "0.875rem" }}>
          Configure salary structure, statutory deductions and default payroll values.
        </p>
      </div>

      {successMsg && (
        <div style={{ background: "#DCFCE7", color: "#166534", padding: "10px 16px", borderRadius: "8px", fontSize: "0.875rem", fontWeight: 600, marginBottom: "1.5rem" }}>
          ✓ {successMsg}
        </div>
      )}

      <form onSubmit={handleSave}>
        {/* 1. Salary Configuration */}
        <div style={{ marginBottom: "2rem" }}>
          <h3 style={sectionTitleStyle}>Salary Configuration</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "1.25rem",
            }}
          >
            <div>
              <label style={labelStyle}>Full-Time Base Salary (₹/Month)</label>
              <input
                type="number"
                name="fullTimeSalary"
                value={settings.fullTimeSalary}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Fresher Stipend (₹/Month)</label>
              <input
                type="number"
                name="fresherStipend"
                value={settings.fresherStipend}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Experienced Stipend (₹/Month)</label>
              <input
                type="number"
                name="experiencedStipend"
                value={settings.experiencedStipend}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Deferred Stipend (₹/Month)</label>
              <input
                type="number"
                name="deferredStipend"
                value={settings.deferredStipend}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* 2. Statutory Settings */}
        <div style={{ marginBottom: "2rem" }}>
          <h3 style={sectionTitleStyle}>Statutory Settings</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.25rem",
            }}
          >
            <div>
              <label style={labelStyle}>Employee PF (%)</label>
              <input
                type="number"
                name="pfEmployee"
                value={settings.pfEmployee}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Employer PF (%)</label>
              <input
                type="number"
                name="pfEmployer"
                value={settings.pfEmployer}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* 3. Additional Settings */}
        <div style={{ marginBottom: "2.5rem" }}>
          <h3 style={sectionTitleStyle}>Additional Settings</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.25rem",
            }}
          >
            <div>
              <label style={labelStyle}>Overtime Rate (₹/Hour)</label>
              <input
                type="number"
                name="overtimeRate"
                value={settings.overtimeRate}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Default Bonus (₹)</label>
              <input
                type="number"
                name="bonus"
                value={settings.bonus}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div>
          <button
            type="submit"
            disabled={saving}
            style={{
              background: "#164231",
              color: "#FFFFFF",
              border: "none",
              borderRadius: "8px",
              padding: "10px 26px",
              fontSize: "0.875rem",
              fontWeight: 700,
              cursor: saving ? "not-allowed" : "pointer",
              transition: "background 0.15s ease",
            }}
          >
            {saving ? "Saving & Updating Payrolls..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
